import { useState, useEffect, useRef, Dispatch, SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ProgressBar, TopWrapper, TypeDot } from './style';
import { H3 } from '@/components/UI/Typography/Heading';
import { convertToFileSize } from '@/utils/convert';
import { StorageAlmostFullModal, StorageFullModal } from '@/components/DocLibrary/Modal';
import { getStorageSpace } from '@/api/auth.api';
import { Flex } from '@/layout';
import { Text } from '../Typography/Text';
import { Button } from '../Button';
import { calculateStorageBreakdown, toVisiblePercentage } from './storageBreakdown';

interface StorageBarProps {
  refreshKey?: number;
  showStorageAlmostFullModal: boolean;
  showStorageFullModal: boolean;
  setShowStorageAlmostFullModal: Dispatch<SetStateAction<boolean>>;
  setShowStorageFullModal: Dispatch<SetStateAction<boolean>>;
}

export const StorageBar = ({
  refreshKey = 0,
  showStorageAlmostFullModal,
  showStorageFullModal,
  setShowStorageAlmostFullModal,
  setShowStorageFullModal,
}: StorageBarProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const [remain, setRemain] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [used, setUsed] = useState<number>(0);

  const [videoUsed, setVideoUsed] = useState<number>(0);
  const [documentUsed, setDocumentUsed] = useState<number>(0);
  const [blockchainUsed, setBlockchainUsed] = useState<number>(0);
  const [storageStatus, setStorageStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const requestVersion = useRef(0);

  const [showNewFeature] = useState<boolean>(true);

  const converPersentage = () => {
    // 計算使用量百分比
    const persentage = (used / total) * 100;
    return persentage;
  };

  const checkStorage = useCallback(async () => {
    const version = ++requestVersion.current;
    setStorageStatus('loading');
    try {
      const res = (await getStorageSpace()).data;
      if (version !== requestVersion.current) return;
      if (res.success) {
        if (showNewFeature) {
          setTotal(res.data.total);

          // 取得各分類使用量；blockchain_size 是已產生 blockchain hash 的 Deal Document
          const meetingSize = res.data.meeting_size || 0;
          const blockchainSize = res.data.blockchain_size || 0;
          const usedSize = res.data.used || 0;
          const breakdown = calculateStorageBreakdown(usedSize, meetingSize, blockchainSize);

          setVideoUsed(toVisiblePercentage(breakdown.video, res.data.total));
          setDocumentUsed(toVisiblePercentage(breakdown.document, res.data.total));
          setBlockchainUsed(toVisiblePercentage(breakdown.blockchain, res.data.total));
          setUsed(usedSize);
        } else {
          setTotal(res.data.total);
          setUsed(res.data.used);
          setRemain(res.data.remaining);
        }

        // 檢查是否快滿了, 條件為 90%
        if (res.data.used / res.data.total > 0.9) {
          setShowStorageAlmostFullModal(true);
        }
        setStorageStatus('ready');
      } else {
        setStorageStatus('error');
      }
    } catch (error) {
      if (version === requestVersion.current) setStorageStatus('error');
    }
  }, [setShowStorageAlmostFullModal]);

  useEffect(() => {
    void checkStorage();
    return () => { requestVersion.current += 1; };
  }, [checkStorage, refreshKey]);

  return (
    <TopWrapper>
      <StorageAlmostFullModal
        showModal={showStorageAlmostFullModal}
        setShowModal={setShowStorageAlmostFullModal}
        remain={convertToFileSize(remain)}
      />
      <StorageFullModal showModal={showStorageFullModal} setShowModal={setShowStorageFullModal} />
      <div className="title">
        <div className="icon">
          <img src="/images/docLibrary/cloud.svg" alt="" />
        </div>
        <span>{t('pages.documentLibrary.storage.title')}</span>
      </div>
      <div className="link">
        <Link to="/profile/subscriptionDetail">{t('pages.documentLibrary.storage.getMoreStorage')}</Link>
      </div>
      <div className="text">
        {storageStatus === 'loading' ? (
          <H3 role="status">{t('loading')}</H3>
        ) : storageStatus === 'error' ? (
          <Flex alignItems="center" gridGap="12px">
            <Text role="alert">{t('pages.documentLibrary.storage.loadFailed')}</Text>
            <Button type="button" variant="primary-invert" onClick={() => void checkStorage()}>
              {t('pages.documentLibrary.storage.retry')}
            </Button>
          </Flex>
        ) : (
          <H3>
            {t('pages.documentLibrary.storage.used', {
              used: convertToFileSize(used),
              total: convertToFileSize(total),
            })}
          </H3>
        )}
      </div>
      <ProgressBar>
        {storageStatus !== 'ready' ? null : showNewFeature ? (
          <Flex position={'relative'}>
            <div className="orange" style={{ width: `${documentUsed}%` }}></div>
            <div className="green" style={{ width: `${videoUsed}%` }}></div>
            <div className="blue" style={{ width: `${blockchainUsed}%` }}></div>
          </Flex>
        ) : (
          <div className="used" style={{ width: `${converPersentage()}%` }}></div>
        )}
      </ProgressBar>
      {showNewFeature && (
        <Flex marginTop={'20px'} gridGap={'20px'}>
          <Flex gridGap={'8px'}>
            <TypeDot color="orange" />
            <Text>{t('pages.documentLibrary.storage.document')}</Text>
          </Flex>
          <Flex gridGap={'8px'}>
            <TypeDot color="green" />
            <Text>{t('pages.documentLibrary.storage.video')}</Text>
          </Flex>
          <Flex gridGap={'8px'}>
            <TypeDot color="blue" />
            <Text>{t('pages.documentLibrary.storage.blockchain')}</Text>
          </Flex>
        </Flex>
      )}
    </TopWrapper>
  );
};
